import ExternalAssigneeLabel from '@/components/serviceTicket/assignment/ExternalAssigneeLabel';
import MorePopover from '@/components/ui/MorePopover';
import TagList from '@/components/ui/TagList';
import TableColumnDate from '@/components/ui/table/TableColumnDate';
import { US_STATE_OPTIONS } from '@/constants/app/usStates';
import { validateFilterById } from '@/helpers/api';
import type { Tag } from '@/types';
import type { ServiceTicket } from '@/types/serviceTicket';
import { ClickableText } from '@bitstopco/bitstop-theme';
import type {
  ApiListingRequest,
  Columns,
  TableToolbarFilterConfig,
} from '@bitstopco/bitstop-theme';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// Maps a route status to a MUI chip color, mirroring the assign-route modal.
const getRouteStatusColor = (status?: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'planning':
    case 'planned':
      return 'warning';
    default:
      return 'default';
  }
};

const formatRouteStatus = (status?: string) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') : 'Unknown';

export const serviceTicketsColumns = (
  techniciansMap?: Map<
    string | number,
    { first_name?: string; last_name?: string; email?: string }
  >,
  onAddToRoute?: (ticketId: string) => void,
  usersMap?: Map<string | number, { first_name?: string; last_name?: string; email?: string }>,
): Columns<ServiceTicket> => ({
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      value: 'display_id',
      label: 'Ticket ID',
      valueMapper: (value: ServiceTicket['display_id'], ticket: ServiceTicket) => {
        if (!value) return '-';
        return (
          <ClickableText
            text={value?.toString()}
            name="Ticket ID"
            clipboardValue={value.toString()}
            to={`/service-ticket/${ticket.id}`}
            overrideTooltipLinkText="Go to Ticket Details"
          />
        );
      },
    },
    {
      value: 'case_display_id',
      label: 'Case ID',
      valueMapper: (_value: ServiceTicket['case_display_id'], ticket: ServiceTicket) => {
        if (!ticket.case_display_id || !ticket.case_id) return '-';
        return (
          <ClickableText
            text={`${ticket.case_display_id}`}
            name="Case ID"
            clipboardValue={ticket.case_display_id.toString()}
            to={`/case/${ticket.case_id}`}
            overrideTooltipLinkText="Go to Case Details"
          />
        );
      },
    },
    {
      value: 'atm_id',
      label: 'Machine ID',
      valueMapper: (value: ServiceTicket['atm_id']) => {
        if (!value) return '-';

        return (
          <ClickableText
            fixIcon
            text={String(value)}
            to={`/machine/${value}`}
            clipboardValue={value.toString()}
            name="Machine ID"
            overrideTooltipLinkText="Go to Machine"
          />
        );
      },
    },
    {
      value: 'status',
      label: 'Status',
      valueMapper: (value: ServiceTicket['status']) => {
        if (!value) return '-';

        const getStatusColor = (status: string) => {
          switch (status) {
            case 'open':
              return 'error';
            case 'in_progress':
              return 'warning';
            case 'resolved':
              return 'success';
            case 'planned':
              return 'info';
            case 'completed':
              return 'success';
            case 'cancelled':
              return 'default';
            default:
              return 'default';
          }
        };

        const getStatusLabel = (status: string) => {
          switch (status) {
            case 'in_progress':
              return 'In Progress';
            default:
              return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
          }
        };

        return (
          <Chip
            component="span"
            label={getStatusLabel(value)}
            color={getStatusColor(value) as any}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        );
      },
    },
    {
      value: 'location_id',
      label: 'Location ID',
      valueMapper: (value: ServiceTicket['location_id']) => {
        if (!value) return '-';

        return (
          <ClickableText
            fixIcon
            text={String(value)}
            to={`/location/${value}`}
            clipboardValue={value.toString()}
            name="Location ID"
          />
        );
      },
    },
    {
      value: 'street_address',
      label: 'Street Address',
      valueMapper: (value: ServiceTicket['street_address']) =>
        value ? (
          <Typography variant="body2" sx={{ maxWidth: 250 }}>
            {value}
          </Typography>
        ) : (
          '-'
        ),
    },
    {
      value: 'address2',
      label: 'Address 2',
      valueMapper: (value: ServiceTicket['address2']) =>
        value ? (
          <Typography variant="body2" sx={{ maxWidth: 250 }}>
            {value}
          </Typography>
        ) : (
          '-'
        ),
    },
    {
      value: 'city',
      label: 'City',
      valueMapper: (value: ServiceTicket['city']) => value || '-',
    },
    {
      value: 'state',
      label: 'State',
      valueMapper: (value: ServiceTicket['state']) => value || '-',
    },
    {
      value: 'zipcode',
      label: 'Zip Code',
      valueMapper: (value: ServiceTicket['zipcode']) => value || '-',
    },
    {
      value: 'country',
      label: 'Country',
      valueMapper: (value: ServiceTicket['country']) => value || '-',
    },
    {
      value: 'technician_id',
      label: 'Technician',
      valueMapper: (value: ServiceTicket['technician_id'], ticket: ServiceTicket) => {
        if (ticket.ops_service_id) {
          return <ExternalAssigneeLabel opsServiceName={ticket.ops_service_name} />;
        }
        if (!value) return '-';

        // Try to get technician name from map
        const technician = techniciansMap?.get(value);
        if (technician) {
          const name =
            technician.first_name && technician.last_name
              ? `${technician.first_name} ${technician.last_name}`
              : technician.first_name ||
                technician.last_name ||
                technician.email ||
                value.toString();
          return (
            <Tooltip title={value.toString()} arrow>
              <Typography variant="body2">{name}</Typography>
            </Tooltip>
          );
        }

        // Fallback to ID if technician not found
        return (
          <ClickableText
            name="Technician"
            text={value.toString()}
            clipboardValue={value.toString()}
          />
        );
      },
    },
    {
      value: 'start_date',
      label: 'Start Date',
      valueMapper: (value: ServiceTicket['start_date'], data: any) =>
        value ? (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
            auxiliaryDate={
              data.local_start_date && data.local_time_zone
                ? {
                    value: data.local_start_date,
                    options: {
                      timeZone: data.local_time_zone,
                      normalizeAsUTC: false,
                      performUtcToZonedTime: false,
                    },
                  }
                : undefined
            }
          />
        ) : (
          '-'
        ),
    },
    {
      value: 'created_by',
      label: 'Created By',
      valueMapper: (value: ServiceTicket['created_by']) => {
        if (!value) return '-';

        // Try both string and number lookups since IDs can be either
        const user =
          usersMap?.get(value) || usersMap?.get(Number(value)) || usersMap?.get(String(value));

        if (user) {
          const name =
            user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : user.first_name || user.last_name || user.email || value.toString();
          return <Typography variant="body2">{name}</Typography>;
        }

        // Fallback to just showing the ID as plain text
        return <Typography variant="body2">{value}</Typography>;
      },
    },
    {
      label: 'Location Tags',
      labelSx: {
        minWidth: '250px',
      },
      valueMapper: (_, data) => (
        <TagList selectedTagIds={data.tag_ids} tagsCollection={data.tags as Tag[] | undefined} />
      ),
      hideSort: true,
    },
    {
      label: 'Tasks',
      labelSx: {
        minWidth: '200px',
      },
      valueMapper: (_: any, data: ServiceTicket) => {
        if (!data.tasks || data.tasks.length === 0) return '-';
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {data.tasks.map((task) => (
              <Chip
                key={task.id}
                label={task.task_type_name || 'Unknown Task'}
                size="small"
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>
        );
      },
      hideSort: true,
    },
    {
      label: 'Routes',
      labelSx: {
        minWidth: '200px',
      },
      valueMapper: (_: any, data: ServiceTicket) => {
        if (!data.routes || data.routes.length === 0) return '-';
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {data.routes.map((route) => (
              <Tooltip
                key={route.id ?? route.route_id}
                title={`${route.name ? `${route.name} — ` : ''}Status: ${formatRouteStatus(
                  route.route_status,
                )}`}
                arrow
              >
                <Chip
                  component={RouterLink}
                  to={`/route/${route.route_id}`}
                  clickable
                  label={`#${route.display_id ?? route.route_id}`}
                  size="small"
                  color={getRouteStatusColor(route.route_status) as any}
                  sx={{ fontWeight: 500 }}
                />
              </Tooltip>
            ))}
          </Box>
        );
      },
      hideSort: true,
    },
    {
      value: 'created_at',
      label: 'Created At',
      valueMapper: (value: ServiceTicket['created_at'], data: any) =>
        value ? (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
            auxiliaryDate={
              data.local_created_at && data.local_time_zone
                ? {
                    value: data.local_created_at,
                    options: {
                      timeZone: data.local_time_zone,
                      normalizeAsUTC: false,
                      performUtcToZonedTime: false,
                    },
                  }
                : undefined
            }
          />
        ) : (
          '-'
        ),
    },
    {
      value: 'last_updated_at',
      label: 'Last Updated',
      valueMapper: (value: ServiceTicket['last_updated_at'], data: any) =>
        value ? (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
            auxiliaryDate={
              data.local_last_updated_at && data.local_time_zone
                ? {
                    value: data.local_last_updated_at,
                    options: {
                      timeZone: data.local_time_zone,
                      normalizeAsUTC: false,
                      performUtcToZonedTime: false,
                    },
                  }
                : undefined
            }
          />
        ) : (
          '-'
        ),
    },
    // Actions stays the last column in the table.
    ...(onAddToRoute
      ? [
          {
            value: 'actions',
            label: 'Actions',
            valueSx: {
              minWidth: 100,
              maxWidth: 100,
            },
            valueMapper: (_: any, ticket: ServiceTicket) => (
              <MorePopover
                items={['Add to Route']}
                onClick={(index) => {
                  if (index === 0 && ticket.id) {
                    onAddToRoute(ticket.id);
                  }
                }}
              />
            ),
          },
        ]
      : []),
  ],
});

/**
 * Helper to format status values for display
 */
const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Normalize status value to snake_case lowercase
 * e.g., "InProgress" -> "in_progress", "Resolved" -> "resolved"
 */
const normalizeStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    InProgress: 'in_progress',
    Resolved: 'resolved',
    Closed: 'closed',
    Planned: 'planned',
    Completed: 'completed',
    Cancelled: 'cancelled',
  };
  return (
    statusMap[status] ||
    status
      .toLowerCase()
      .replace(/([A-Z])/g, '_$1')
      .replace(/^_/, '')
  );
};

/**
 * Format task type for display (e.g., "MachineOffline" -> "Machine Offline")
 */
const formatTaskType = (type: string): string => {
  return type.replace(/([A-Z])/g, ' $1').trim();
};

/**
 * Generate task type filter options from API resources
 */
const getTaskTypeOptions = (types?: string[]) => {
  if (!types || types.length === 0) {
    return [];
  }
  return types.map((type) => ({
    label: formatTaskType(type),
    value: type,
  }));
};

/**
 * Create service tickets filters config with dynamic status options
 */
export const createServiceTicketsFiltersConfig = (
  statuses?: string[],
  technicians?: Array<{
    id: string | number;
    first_name?: string;
    last_name?: string;
    email?: string;
  }>,
  _users?: Array<{
    id: string | number;
    first_name?: string;
    last_name?: string;
    email?: string;
  }>,
  taskTypes?: string[],
  priorities?: string[],
  opsServices?: Array<{ id?: string | null; name?: string | null; is_active?: boolean }>,
): TableToolbarFilterConfig[] => {
  const statusOptions = statuses?.map((status) => ({
    label: formatStatus(status),
    value: normalizeStatus(status),
  })) || [
    { label: 'Planned', value: 'planned' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  const priorityOptions = priorities?.map((priority) => ({
    label: priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase(),
    value: priority.toLowerCase(),
  })) || [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Critical', value: 'critical' },
  ];

  return [
    {
      property: 'id',
      type: 'select-ids',
      placeholder: 'Ticket',
      inputPlaceholder: 'Ticket ID',
      label: 'Ticket ID',
      validate: async () => null,
      mapperSelectedValue: (value: string) => `Ticket ID ${value}`,
      mapperSelectedValues: (count: number) => `Ticket (${count})`,
    },
    {
      property: 'atm_id',
      type: 'select-ids',
      placeholder: 'Machine',
      inputPlaceholder: 'Machine ID',
      label: 'Machine ID',
      validate: validateFilterById('machine'),
      mapperSelectedValue: (value: string) => `Machine ${value}`,
      mapperSelectedValues: (count: number) => `Machine ID (${count})`,
    },
    {
      property: 'location_id',
      type: 'select-ids',
      placeholder: 'Location',
      inputPlaceholder: 'Location ID',
      label: 'Location ID',
      validate: (value: string) => {
        // getLocations is a POST listing endpoint -- it needs a full ApiListingRequest body,
        // not the bare id, or axios sends a raw string with no JSON content-type and athena
        // responds 415. LocationId is an int on athena's side and its JSON options don't allow
        // reading numbers from strings, so a quoted "5" fails to deserialize there -- send a
        // real number.
        const filters: ApiListingRequest = {
          filter: {
            operator: 'and',
            operands: [
              {
                operator: 'equal',
                property: 'location_id',
                value: Number(value),
              },
            ],
          },
          pagination: {
            current_page: 1,
            items_per_page: 1,
            sort_ascending: false,
            sort_by: 'location_id',
          },
        };

        return validateFilterById('location')(filters);
      },
      mapperSelectedValue: (value: string) => `Location ${value}`,
      mapperSelectedValues: (count: number) => `Location ID (${count})`,
    },
    {
      property: 'case_display_id',
      type: 'select-ids',
      placeholder: 'Case',
      inputPlaceholder: 'Case Display ID',
      label: 'Case ID',
      validate: async () => null,
      mapperSelectedValue: (value: string) => `Case #${value}`,
      mapperSelectedValues: (count: number) => `Case ID (${count})`,
    },
    {
      property: 'route_ids',
      type: 'select-ids',
      placeholder: 'Route',
      inputPlaceholder: 'Route ID',
      label: 'Route ID',
      validate: async () => null,
      mapperSelectedValue: (value: string) => `Route #${value}`,
      mapperSelectedValues: (count: number) => `Route ID (${count})`,
    },
    {
      property: 'street_address',
      type: 'select-ids',
      placeholder: 'Address',
      inputPlaceholder: 'Enter address',
      label: 'Address',
      operator: 'contains',
      validate: async () => null,
      mapperSelectedValue: (value: string) => value,
      mapperSelectedValues: (count: number) => `Address (${count})`,
    },
    {
      property: 'address2',
      type: 'select-ids',
      placeholder: 'Address 2',
      inputPlaceholder: 'Enter address 2',
      label: 'Address 2',
      operator: 'contains',
      validate: async () => null,
      mapperSelectedValue: (value: string) => value,
      mapperSelectedValues: (count: number) => `Address 2 (${count})`,
    },
    {
      property: 'city',
      type: 'select-ids',
      placeholder: 'City',
      inputPlaceholder: 'Enter city',
      label: 'City',
      operator: 'contains',
      validate: async () => null,
      mapperSelectedValue: (value: string) => value,
      mapperSelectedValues: (count: number) => `City (${count})`,
    },
    {
      property: 'state',
      label: 'State',
      type: 'select',
      placeholder: 'State',
      options: US_STATE_OPTIONS,
      multiple: true,
    },
    {
      property: 'zipcode',
      type: 'select-ids',
      placeholder: 'Zip Code',
      inputPlaceholder: 'Enter zip code',
      label: 'Zip Code',
      operator: 'contains',
      validate: async () => null,
      mapperSelectedValue: (value: string) => value,
      mapperSelectedValues: (count: number) => `Zip Code (${count})`,
    },
    {
      property: 'status',
      label: 'Any Status',
      type: 'select',
      placeholder: 'Any Status',
      options: statusOptions,
      multiple: true,
    },
    {
      property: 'priority',
      label: 'Priority',
      type: 'select',
      placeholder: 'Priority',
      options: priorityOptions,
      multiple: true,
    },
    {
      property: 'technician_id',
      type: 'select',
      placeholder: 'Technician',
      label: 'Technician',
      operator: 'equal',
      options:
        technicians?.map((tech) => ({
          label:
            tech.first_name && tech.last_name
              ? `${tech.first_name} ${tech.last_name}`
              : tech.email || `Technician ${tech.id}`,
          value: tech.id.toString(),
        })) || [],
      multiple: true,
      searchable: true,
      searchPlaceholder: 'Search by technician name',
    },
    {
      property: 'ops_service_id',
      type: 'select',
      placeholder: 'Ops Service',
      label: 'Ops Service',
      operator: 'equal',
      // Inactive vendors stay listed so tickets already assigned to them can still be found.
      options:
        opsServices
          ?.filter((opsService) => opsService.id)
          .map((opsService) => ({
            label: opsService.is_active
              ? opsService.name || 'Unnamed Ops Service'
              : `${opsService.name || 'Unnamed Ops Service'} (inactive)`,
            value: opsService.id as string,
          })) || [],
      multiple: true,
      searchable: true,
      searchPlaceholder: 'Search by ops service name',
    },
    {
      property: 'location_tag_ids',
      operator: 'contains',
      type: 'select',
      label: 'Location Tags',
      placeholder: 'Location Tags',
      searchable: true,
      multiple: true,
      showFooter: true,
      options: [],
      searchPlaceholder: 'Search by tag name',
    },
    {
      property: 'task_type_names',
      label: 'Task Types',
      placeholder: 'Task Types',
      type: 'select',
      operator: 'contains',
      options: getTaskTypeOptions(taskTypes),
      multiple: true,
      searchable: true,
      searchPlaceholder: 'Search by task type',
    },
  ];
};

// Default export for backwards compatibility
export const SERVICE_TICKETS_FILTERS_CONFIG = createServiceTicketsFiltersConfig();

export const SERVICE_TICKETS_ACTION_MENU_ITEMS = [
  {
    label: 'Export as CSV',
    value: 'csv',
  },
  {
    label: 'Export as Excel',
    value: 'excel',
  },
];
